"use server";

import { revalidatePath } from "next/cache";
import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { getAppUrl } from "@/lib/config";
import {
  createE2EUser,
  E2E_SESSION_COOKIE,
  isE2ETestMode,
} from "@/lib/e2e-store";
import {
  FIND_INTENT_COOKIE,
  parseFindIntent,
  serializeFindIntent,
} from "@/lib/sample-run";
import {
  HONEYPOT_FIELD,
  screenSignupEmail,
  verifySignupFormToken,
} from "@/lib/signup-guard";
import { TURNSTILE_RESPONSE_FIELD, verifyTurnstile } from "@/lib/turnstile";
import {
  createSupabaseServerClient,
  getSupabaseAdminClient,
} from "@/lib/supabase/server";

function getSafeNextPath(value: FormDataEntryValue | null) {
  const next = String(value || "/dashboard");

  if (!next.startsWith("/") || next.startsWith("//")) {
    return "/dashboard";
  }

  return next;
}

function buildAuthErrorPath(
  path: "/login" | "/register",
  message: string,
  next: string,
) {
  const params = new URLSearchParams({
    error: message,
    next,
  });

  return `${path}?${params.toString()}`;
}

/**
 * Signup is the one unauthenticated path that creates a row, so it is screened
 * before any of it runs. See `lib/signup-guard` for what each check is.
 */
const MAX_SIGNUP_ATTEMPTS_PER_HOUR = 10;
const SIGNUP_ATTEMPT_WINDOW_MS = 60 * 60 * 1000;

type SignupBlock =
  /** Answer as if it worked; tell the caller nothing. */
  | { kind: "silent"; reason: string }
  /** Too many attempts from this network. */
  | { kind: "throttled"; reason: string }
  /** The address may not be used to open an account. */
  | { kind: "email"; reason: string };

function clientIp(headerStore: Headers): string | null {
  const forwarded = headerStore.get("x-forwarded-for");

  if (forwarded) {
    return forwarded.split(",")[0]?.trim() || null;
  }

  return headerStore.get("x-real-ip");
}

async function requestIp(): Promise<string | null> {
  try {
    return clientIp(await headers());
  } catch {
    return null;
  }
}

async function guardSignup(formData: FormData): Promise<SignupBlock | null> {
  // A person never sees this field, so anything in it is a form filler.
  if (String(formData.get(HONEYPOT_FIELD) || "").trim()) {
    return { kind: "silent", reason: "honeypot" };
  }

  const tokenVerdict = verifySignupFormToken(
    String(formData.get("formToken") || "") || null,
  );

  if (tokenVerdict !== "ok") {
    return { kind: "silent", reason: `token_${tokenVerdict}` };
  }

  // Screen the address before writing anything: an obvious throwaway should not
  // even count against the network's rate budget.
  const email = String(formData.get("email") || "");
  const emailReason = screenSignupEmail(email);

  if (emailReason) {
    return { kind: "email", reason: emailReason };
  }

  // Per-IP ceiling. Fails open on purpose — a database hiccup must never stop a
  // real person from signing up; the challenge (Turnstile) is the real defence.
  try {
    const ip = await requestIp();

    if (ip) {
      const admin = getSupabaseAdminClient();
      const since = new Date(
        Date.now() - SIGNUP_ATTEMPT_WINDOW_MS,
      ).toISOString();

      const { count } = await admin
        .from("signup_attempts")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", since);

      if ((count ?? 0) >= MAX_SIGNUP_ATTEMPTS_PER_HOUR) {
        return { kind: "throttled", reason: "ip_rate" };
      }

      await admin.from("signup_attempts").insert({ ip });
    }
  } catch {
    // Ignore: never let throttling break signup.
  }

  return null;
}

/** The exact state a real signup lands on, so a blocked bot learns nothing. */
function pretendConfirmationSent(email: string) {
  redirect(
    `/login?message=${encodeURIComponent(
      `Account created. We sent a confirmation link to ${email} — open it to finish signing up. Check your spam folder if it has not arrived within a few minutes.`,
    )}`,
  );
}

const THROTTLED_MESSAGE =
  "Too many sign-up attempts from this network just now. Please try again in a little while.";
const BLOCKED_EMAIL_MESSAGE =
  "That address cannot be used to open an account. Please use a permanent mailbox on your own domain.";
const CHALLENGE_MESSAGE =
  "We could not verify that you are a person. Please try again.";

export async function signInWithPassword(formData: FormData) {
  if (isE2ETestMode()) {
    const email = String(formData.get("email") || "");
    const next = getSafeNextPath(formData.get("next"));
    const user = createE2EUser(email);
    const cookieStore = await cookies();
    cookieStore.set(E2E_SESSION_COOKIE, user.id, {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
    });
    redirect(next);
  }

  const supabase = await createSupabaseServerClient();
  const email = String(formData.get("email") || "");
  const password = String(formData.get("password") || "");
  const next = getSafeNextPath(formData.get("next"));

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) redirect(buildAuthErrorPath("/login", error.message, next));

  revalidatePath("/", "layout");
  redirect(next);
}

async function rememberFindIntent(formData: FormData) {
  const intent = parseFindIntent(
    String(formData.get("query") || ""),
    String(formData.get("location") || ""),
  );

  if (!intent) return;

  const cookieStore = await cookies();

  cookieStore.set(FIND_INTENT_COOKIE, serializeFindIntent(intent), {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 30,
  });
}

export async function signUpWithPassword(formData: FormData) {
  if (isE2ETestMode()) {
    const email = String(formData.get("email") || "");
    const next = getSafeNextPath(formData.get("next"));
    await rememberFindIntent(formData);
    const user = createE2EUser(email);
    const cookieStore = await cookies();
    cookieStore.set(E2E_SESSION_COOKIE, user.id, {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
    });
    redirect(next);
  }

  const email = String(formData.get("email") || "");
  const password = String(formData.get("password") || "");
  const next = getSafeNextPath(formData.get("next"));

  await rememberFindIntent(formData);

  const block = await guardSignup(formData);

  if (block) {
    if (block.kind === "silent") pretendConfirmationSent(email);

    redirect(
      buildAuthErrorPath(
        "/register",
        block.kind === "throttled" ? THROTTLED_MESSAGE : BLOCKED_EMAIL_MESSAGE,
        next,
      ),
    );
  }

  const challenge = await verifyTurnstile({
    token: String(formData.get(TURNSTILE_RESPONSE_FIELD) || "") || null,
    remoteip: await requestIp(),
    expectedAction: "signup",
  });

  if (challenge === "failed") {
    redirect(buildAuthErrorPath("/register", CHALLENGE_MESSAGE, next));
  }

  const supabase = await createSupabaseServerClient();

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${getAppUrl()}/auth/confirm?next=${encodeURIComponent(next)}`,
    },
  });

  if (error) redirect(buildAuthErrorPath("/register", error.message, next));

  // With confirmation on, signUp returns a user but no session. Sending the
  // visitor to `next` drops them through the dashboard's auth gate and back
  // onto a bare login form with nothing explaining how they got there — which
  // is where the overwhelming majority of signups used to stop. Say what
  // happens next instead. A blocked bot is sent to the same place, so it cannot
  // tell the two apart.
  if (!data.session) {
    pretendConfirmationSent(email);
  }

  redirect(next);
}

export async function sendMagicLink(formData: FormData) {
  const email = String(formData.get("email") || "");
  const next = getSafeNextPath(formData.get("next"));

  const block = await guardSignup(formData);

  if (block) {
    if (block.kind === "silent") {
      redirect("/login?message=Check your email for a magic link.");
    }

    redirect(
      `/login?error=${encodeURIComponent(
        block.kind === "throttled" ? THROTTLED_MESSAGE : BLOCKED_EMAIL_MESSAGE,
      )}`,
    );
  }

  const supabase = await createSupabaseServerClient();

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: `${getAppUrl()}/auth/confirm?next=${next}` },
  });

  if (error) redirect(`/login?error=${encodeURIComponent(error.message)}`);
  redirect("/login?message=Check your email for a magic link.");
}

export async function signInWithGoogle(formData: FormData) {
  const supabase = await createSupabaseServerClient();
  const next = getSafeNextPath(formData.get("next"));

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${getAppUrl()}/auth/confirm?next=${encodeURIComponent(next)}`,
    },
  });

  if (error) redirect(`/login?error=${encodeURIComponent(error.message)}`);
  if (!data.url) redirect("/login?error=Could not start Google sign-in.");

  redirect(data.url);
}

export async function signInWithFacebook(formData: FormData) {
  const supabase = await createSupabaseServerClient();
  const next = getSafeNextPath(formData.get("next"));

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "facebook",
    options: {
      redirectTo: `${getAppUrl()}/auth/confirm?next=${encodeURIComponent(next)}`,
    },
  });

  if (error) redirect(`/login?error=${encodeURIComponent(error.message)}`);
  if (!data.url) redirect("/login?error=Could not start Facebook sign-in.");

  redirect(data.url);
}

export async function signOut() {
  if (isE2ETestMode()) {
    const cookieStore = await cookies();
    cookieStore.delete(E2E_SESSION_COOKIE);
    redirect("/");
  }

  const supabase = await createSupabaseServerClient();
  await supabase.auth.signOut();
  revalidatePath("/", "layout");
  redirect("/");
}
